import React, { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Loader2, PencilLine } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const URL_ = `${API}/api/admin/demo-manager/authorizer/hope-period-rename`;

// One-time: rename Hope of Detroit's placeholder period. Preview first, then Apply.
export const HopeRenameCard = ({ headers }) => {
  const [from, setFrom] = useState("Fall 2026 Sample Period");
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState("");
  const run = async (apply) => {
    setBusy(apply ? "apply" : "preview");
    try {
      const q = apply ? `?dry_run=false&from_label=${encodeURIComponent(from)}` : "";
      const { data } = await axios.post(`${URL_}${q}`, {}, { headers });
      setRep(data);
      if (apply) toast.success(`Renamed ${data.renamed.length} diagnostic(s), ${data.renamed_periods.length} period(s)`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); } finally { setBusy(""); }
  };
  const labels = [...(rep?.diagnostics || []).map((d) => ({ k: d.id, name: d.period_label, kind: `Diagnostic · ${d.perspective || "school"} · ${d.status || ""}` })),
    ...(rep?.periods || []).map((p) => ({ k: p.id, name: p.name, kind: `Period · ${p.school_count} school(s)` }))];
  return (
    <div className="mt-8 rounded-xl border border-[#e5e7eb] bg-white p-5" data-testid="hope-rename-card">
      <div className="flex items-center gap-2 mb-1"><PencilLine className="h-4 w-4 text-[#007a9e]" /><h2 className="text-base font-bold text-[#111827]">Rename Hope period</h2></div>
      <p className="text-[13px] text-[#6b7280]">Hope of Detroit: rename the placeholder period to <b className="text-[#111827]">Fall 2026 Diagnostic</b>. Preview first; nothing changes until you click Apply.</p>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Input value={from} onChange={(e) => setFrom(e.target.value)} className="max-w-xs" data-testid="hope-rename-from" aria-label="Current placeholder name" />
        <Button variant="outline" disabled={!!busy} onClick={() => run(false)} data-testid="hope-rename-preview">
          {busy === "preview" && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Preview
        </Button>
        <Button className="bg-[#007a9e] hover:bg-[#00607d] text-white" disabled={!!busy || !rep || !from.trim()} onClick={() => run(true)} data-testid="hope-rename-apply">
          {busy === "apply" && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}Apply
        </Button>
      </div>
      {rep && (
        <div className="mt-3 text-[12.5px]" data-testid="hope-rename-result">
          {!labels.length && <p className="text-[#6b7280]">No Hope of Detroit diagnostics or periods found.</p>}
          <ul className="divide-y divide-[#f0f1f3]">
            {labels.map((l) => (
              <li key={l.k} className="flex justify-between gap-3 py-1.5">
                <span className={l.name === from ? "font-semibold text-[#b45309]" : "text-[#111827]"}>{l.name || "(no name)"}</span>
                <span className="text-[#6b7280]">{l.kind}</span>
              </li>
            ))}
          </ul>
          {rep.mode === "apply" && (
            <p className="mt-2 text-[#065f46]" data-testid="hope-rename-applied">
              Renamed {rep.renamed.length} diagnostic(s) and {rep.renamed_periods.length} period(s).
              {rep.skipped_shared_periods?.length > 0 && ` Skipped ${rep.skipped_shared_periods.length} shared period(s).`}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
